/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React, { ChangeEvent } from "react"
import { Alert, Input } from "@duxton-web/ui"
import { AddUniversal } from "@duxton-web/icons"
import { find } from "lodash"
import { actionCreator } from "store/store"
import { Image } from "components/Image"
import { useDispatch, useSelector } from "react-redux"
import { useTranslationsMap } from "common/utils/i18n"
import { storageHelper } from "utils/storage"
import DOMPurify from "dompurify"
import {
  readFileAsDataURL,
  getBase64String,
  getFileType,
  isTmpUrl,
} from "utils/photo_helper"
import { Review } from "types/api/menuFeedback.type"
import { AppState } from "store/store.type"
import { ReportReason } from "store/duck/global.type"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import "./FeedbackReportSubmit.less"

const photoSize = 104

const style = css({
  fontSize: 16,
  ".content": {
    padding: "31px 21px",
    display: "flex",
    flexDirection: "column",
    gap: 30,
  },
  ".header": {
    fontWeight: 500,
    fontSize: 16,
    color: "#1c1c1c",
  },
  ".required": {
    color: "#ff0000",
    marginLeft: "4px",
    fontSize: "14px",
  },
  ".sub-header": {
    fontWeight: 400,
    fontSize: 12,
    color: "#707070",
  },
  ".list": {
    display: "flex",
    flexDirection: "column",
  },
  ".photos": {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
    marginTop: 30,
  },
  ".add-photo-btn": {
    height: photoSize,
    width: photoSize,
    borderRadius: 8,
    border: "1px dashed #00B14F",
    fontSize: 14,
    fontWeight: 500,
    color: "#00B14F",
    backgroundColor: "rgba(0, 177, 79, 0.04)",
    textAlign: "center",
    cursor: "pointer",
  },
})

interface FeedbackReportSubmitProps extends FormComponentProps {
  review?: Review
  reasons?: string[]
  reasonOptions?: ReportReason[]
}

export const FeedbackReportSubmit: React.FC<FeedbackReportSubmitProps> = ({
  form,
  review,
  reasons,
  reasonOptions = [],
}) => {
  const dispatch = useDispatch()

  const countryCode = storageHelper.getUserCountry() || ""
  const textMap = useTranslationsMap({
    reportInfo: `feedback.body.reportInfo.${countryCode}`,
    reasons: "feedback.headings.reasons",
    description: "feedback.headings.description",
    errorRequired: "feedback.labels.required",
    reportDescription: "feedback.placeholder.reportDescription",
    supportingDocuments: "feedback.headings.supportingDocuments",
    optional: "feedback.helperText.optional",
    documentsRequirements: "feedback.body.documentsRequirements",
    documentsExamples: "feedback.body.documentsExamples",
    documentsFormats: "feedback.body.documentsFormats",
    addPhoto: "feedback.button.addPhoto",
  })

  const {
    feedbackDrawer: { files },
  } = useSelector((state: AppState) => state)

  // Create a reference to the hidden file input element
  const hiddenFileInput = React.useRef(null)

  const handleAddPhoto = async () => {
    // @ts-ignore
    hiddenFileInput?.current?.click?.()
  }

  const handleFileInputChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const fileUploaded = event?.target?.files?.[0]
    if (!review || !fileUploaded) return
    const tmpUrl = URL.createObjectURL?.(fileUploaded) ?? ""
    const file: string = await readFileAsDataURL(fileUploaded)
    dispatch(
      actionCreator.feedbackDrawer.uploadReportReviewImg({
        data: getBase64String(file),
        type: getFileType(fileUploaded),
        tmpUrl,
        merchantID: review.merchantID,
      }),
    )

    const imageUrls = files.map(img => img.url)

    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackReport,
      action: EventAction.uploadPhoto,
      labels: {
        REVIEW_ID: review?.reviewID,
        REVIEW_RATING: review?.rating,
        REPORT_REASON: reasons,
        PHOTO_FILENAME: imageUrls,
      },
    })
  }

  const handleDeleteImage = (index: number) => {
    dispatch(actionCreator.feedbackDrawer.removeReportReviewImg(index))
  }

  return (
    <div css={style} data-testid="report-submit" className="report-submit">
      <Alert
        message={
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(textMap.reportInfo),
            }}
          />
        }
        type="info"
        showIcon
        banner
      />
      <div className="content">
        <div className="reasons">
          <div className="header">{textMap.reasons}</div>
          <div className="list">
            {reasons?.map(reason => (
              <div key={reason} className="reason">
                {find(reasonOptions, { code: reason })?.content ?? reason}
              </div>
            ))}
          </div>
        </div>

        <div className="description">
          <div className="header">
            {textMap.description}
            <span className="required">*</span>
          </div>
          {form.getFieldDecorator("description", {
            initialValue: undefined,
            rules: [
              {
                required: true,
                message: textMap.errorRequired,
              },
            ],
          })(
            <Input.TextArea
              data-testid="input-description"
              placeholder={textMap.reportDescription}
            />,
          )}
        </div>

        <div className="documents">
          <div className="header">
            {textMap.supportingDocuments}{" "}
            <span className="sub-header">{textMap.optional}</span>
          </div>
          <div className="sub-header">
            <p>{textMap.documentsRequirements}</p>
            <p>{textMap.documentsExamples}</p>
            <p>{textMap.documentsFormats}</p>
          </div>

          <div className="photos">
            {files.length < 4 && (
              <button
                type="button"
                className="add-photo-btn"
                onClick={handleAddPhoto}
              >
                <AddUniversal style={{ fontSize: 24 }} />
                <div className="label">{textMap.addPhoto}</div>
                <input
                  data-testid="report-file-upload"
                  type="file"
                  accept="image/*"
                  ref={hiddenFileInput}
                  onChange={handleFileInputChange}
                  style={{ display: "none" }}
                />
              </button>
            )}
            {files?.map(({ url: imageUrl }, index) => (
              <Image
                key={imageUrl}
                width={photoSize}
                height={photoSize}
                src={imageUrl}
                opacity={!isTmpUrl(imageUrl) ? 1 : 0.5}
                showDelete
                onClickDelete={() => {
                  handleDeleteImage(index)
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
